// src/components/chat/ChatApp.tsx
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ChatContainer, ErrorMessage, PoweredBy } from './styles/ChatStyles';
import { ChatHeader } from './ChatHeader';  // Make sure this import is correct
import { ChatMessages } from './ChatMessages';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './types/chat';
import { useDomainValidation } from '../../utils/middleware/domainMiddleware';
import { getMessageConfig } from '../../utils/messageConfig';
import { UserInfoForm } from './UserInfoForm';
import { chatAnalytics } from '../../utils/analytics';



interface ChatAppProps {
  headerConfig?: {
    title?: string;
    subtitle?: string;
    logoUrl?: string;
    backgroundColor?: string;
    textColor?: string;
    showPulse?: boolean;
  };
  onClose?: () => void;
  isOpen?: boolean;
}

const ChatApp: React.FC<ChatAppProps> = ({ headerConfig, onClose, isOpen }) => {
  // States
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasShownInactivity, setHasShownInactivity] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userInfo, setUserInfo] = useState<{name: string; email: string; mobile: string; message: string} | null>(null);
  const [formMessage, setFormMessage] = useState<string>('');
  const [formDisplayed, setFormDisplayed] = useState(false);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const messageConfig = getMessageConfig();

  // Get parameters from iframe URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const tenantcode = urlParams.get('tc') || 'nexlydigital';
  const gatherInfo = urlParams.get('gatherinfo') === 'true';
  
  // Extract header customization from URL params
  const headerConfigFromUrl = {
    title: urlParams.get('title') || undefined,
    subtitle: urlParams.get('subtitle') || undefined,
    logoUrl: urlParams.get('logo') || undefined,
    backgroundColor: urlParams.get('bg') || undefined,
    textColor: urlParams.get('color') || undefined,
    showPulse: urlParams.get('pulse') !== 'false'
  };
  
  // Merge props with URL params (URL params take precedence)
  const finalHeaderConfig = {
    ...headerConfig,
    ...Object.fromEntries(Object.entries(headerConfigFromUrl).filter(([_, v]) => v !== undefined))
  };
  
  // Domain validation
  const isValidDomain = useDomainValidation();

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Add system message helper
  const addSystemMessage = useCallback((content: string) => {
    const systemMessage: ChatMessage = {
      id: crypto.randomUUID(),
      content,
      timestamp: new Date(),
      sender: 'system',
      status: 'sent'
    };
    setMessages(prev => [...prev, systemMessage]);
  }, []);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    if (isOpen && !hasShownInactivity) {
      inactivityTimerRef.current = setTimeout(() => {
        if (!hasShownInactivity) {
          addSystemMessage(messageConfig.inactivity);
          setHasShownInactivity(true);
          
          // Send browser notification if page is not visible
          // if (document.hidden && 'Notification' in window) {
          //   if (Notification.permission === 'granted') {
          //     new Notification('Chat Support', {
          //       body: messageConfig.inactivity,
          //       icon: '/favicon.ico',
          //       tag: 'nexly-inactivity'
          //     });
          //   } else if (Notification.permission !== 'denied') {
          //     Notification.requestPermission().then(permission => {
          //       if (permission === 'granted') {
          //         new Notification('Chat Support', {
          //           body: messageConfig.inactivity,
          //           icon: '/favicon.ico',
          //           tag: 'nexly-inactivity'
          //         });
          //       }
          //     });
          //   }
          // }
        }
      }, messageConfig.inactivityDelay);
    }
  }, [isOpen, hasShownInactivity, messageConfig.inactivity, messageConfig.inactivityDelay, addSystemMessage]);

  // Handle welcome message and warm-up call when chat opens
  useEffect(() => {
    if (isOpen) {
      addSystemMessage(messageConfig.welcome);
      
      // Initialize analytics when chat opens for the first time
      chatAnalytics.initialize().then(() => {
        chatAnalytics.trackChatOpen();
        chatAnalytics.trackPageView('/chat-widget');
      });
      
      // Warm-up API call
      const apiEndpoint = import.meta.env.VITE_API_ENDPOINT_URL;
      if (apiEndpoint) {
        try {
          const warmupUrl = new URL('chat-public', apiEndpoint);
          fetch(warmupUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_CLIENT_JWT_SECRET}`,
          'Referer': 'nexlydigital.com',
        },
        body: JSON.stringify({ query: 'warmup', tenantcode }),
      }).catch(() => {}); // Silent fail for warm-up
        } catch (error) {
          console.warn('Invalid API endpoint URL for warmup:', apiEndpoint);
        }
      } else {
        console.warn('VITE_API_ENDPOINT_URL not configured');
      }
      
      // Request notification permission when chat opens
      // if ('Notification' in window && Notification.permission === 'default') {
      //   Notification.requestPermission();
      // }
    }
  }, [isOpen, messageConfig.welcome, addSystemMessage, tenantcode]);

  // Handle inactivity timer
  useEffect(() => {
    if (isOpen) {
      resetInactivityTimer();
    } else {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    }
    
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isOpen, resetInactivityTimer]);

  // Handle user info form submission
  const handleUserInfoSubmit = useCallback((info: {name: string; email: string; mobile: string; message: string}) => {
    setUserInfo(info);
    setShowUserForm(false);
    
    // Track form submission
    chatAnalytics.trackFormSubmission('user_info');
    
    addSystemMessage(`Thank you, ${info.name}! We have received your message: "${info.message}". A member of our team will contact you shortly. Please feel free to continue asking any questions you may have in the meantime.`);
  }, [addSystemMessage]);

  const handleUserInfoClose = useCallback(() => {
    setShowUserForm(false);
  }, []);



  // Handle message sending
  const handleSendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    // Reset inactivity timer on user activity
    resetInactivityTimer();

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      content,
      timestamp: new Date(),
      sender: 'user',
      status: 'sending' as const
    };

    try {
      // Add user message immediately
      setMessages(prev => [...prev, userMessage]);
      setIsTyping(true);
      setError(null);

      // Track message sent
      chatAnalytics.trackMessageSent(content.length);

      // Get AI response using HTTP request
      const userMessageCount = messages.filter(m => m.sender === 'user').length;
      const conversationHistory = messages.filter(m => m.sender === 'user').slice(-5).map(m => ({ role: m.sender, content: m.content }));
      
      const apiEndpoint = import.meta.env.VITE_API_ENDPOINT_URL;
      if (!apiEndpoint) {
        throw new Error('API endpoint not configured');
      }
      
      const chatUrl = new URL('chat-public', apiEndpoint);
      const response = await fetch(chatUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_CLIENT_JWT_SECRET}`,
          'Referer': 'nexlydigital.com',
        },
        body: JSON.stringify({ 
          query: content, 
          tenantcode,
          interactionCount: userMessageCount,
          conversationHistory,
          gatherInfo: gatherInfo && !userInfo,
          formDisplayed
        }),
      }).then(res => {
        if (!res.ok) {
          throw new Error('Network response was not ok');
        }
        return res.json();
      });

      // Handle JSON response format
      const responseText = response.message || '';
      const shouldShowForm = response.showForm === true;
      
      const formatResponse = (text: string) => {
        return text
          .replace(/^"(.*)"$/s, '$1')
          .replace(/\\n/g, '\n\n')
          .replace(/\\t/g, '    ')
          .replace(/\\"/g, '**')
          .replace(/"/g, '**')
          .replace(/\\r/g, '')
          .replace(/\\\\/g, '/');
      };

      // Create AI message with empty content initially
      const aiMessage: ChatMessage = {
        id: crypto.randomUUID(),
        content: '',
        timestamp: new Date(),
        sender: 'ai',
        status: 'sent' as const
      };

      // Update user message status
      setMessages(prev => 
        prev.map(msg => 
          msg.id === userMessage.id 
            ? { ...msg, status: 'sent' as const } 
            : msg
        )
      );

      const fullResponse = formatResponse(responseText);
      
      // If showing form, don't display response in chat bubble
      if (shouldShowForm && gatherInfo && !userInfo) {
        setFormMessage(fullResponse);
        setFormDisplayed(true);
        setTimeout(() => setShowUserForm(true), 500);
      } else {
        // Add AI message and animate typing effect
        setMessages(prev => [...prev, aiMessage]);
        
        const words = fullResponse.split(' ');
        let currentWordIndex = 0;
        
        const typeInterval = setInterval(() => {
          currentWordIndex++;
          const partialContent = words.slice(0, currentWordIndex).join(' ');
          
          setMessages(prev => 
            prev.map(msg => 
              msg.id === aiMessage.id 
                ? { ...msg, content: partialContent }
                : msg
            )
          );
          
          if (currentWordIndex >= words.length) {
            clearInterval(typeInterval);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error getting response:', error);
      setError('Failed to get response. Please try again.');
      
      // Update user message to show error status
      setMessages(prev => 
        prev.map(msg =>
          msg.id === userMessage.id 
            ? { ...msg, status: 'error' as const } 
            : msg
        )
      );
    } finally {
      setIsTyping(false);
    }
  }, [resetInactivityTimer, gatherInfo, messages, userInfo, addSystemMessage]);

  // Handle retry for failed messages
  const handleRetry = useCallback(async (messageId: string) => {
    const messageToRetry = messages.find(msg => msg.id === messageId);
    if (!messageToRetry || messageToRetry.sender !== 'user') return;

    // Filter out the failed message and all subsequent messages
    const messageIndex = messages.findIndex(msg => msg.id === messageId);
    setMessages(prev => prev.slice(0, messageIndex));

    // Retry sending the message
    await handleSendMessage(messageToRetry.content);
  }, [messages, handleSendMessage]);

  if (!isValidDomain) {
    return (
      <ChatContainer>
        <ErrorMessage>
          Unauthorized domain. This chat widget cannot be embedded on this website.
        </ErrorMessage>
      </ChatContainer>
    );
  }

  return (
    <ChatContainer>
      <ChatHeader 
        title={finalHeaderConfig?.title}
        subtitle={finalHeaderConfig?.subtitle}
        logoUrl={finalHeaderConfig?.logoUrl}
        backgroundColor={finalHeaderConfig?.backgroundColor}
        textColor={finalHeaderConfig?.textColor}
        showPulse={finalHeaderConfig?.showPulse}
        onClose={onClose}
       />
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <ChatMessages 
        messages={messages} 
        isTyping={isTyping} 
        onRetry={handleRetry}
        onUserActivity={resetInactivityTimer}
      />
      {showUserForm && (
        <UserInfoForm 
          onSubmit={handleUserInfoSubmit}
          onClose={handleUserInfoClose}
          tenantcode={tenantcode}
          customMessage={formMessage}
          conversationHistory={messages.map(m => ({ 
            role: m.sender === 'ai' ? 'assistant' : m.sender, 
            content: m.content 
          }))}
        />
      )}
      <ChatInput 
        onSendMessage={handleSendMessage} 
        disabled={isTyping || showUserForm}
        onUserActivity={resetInactivityTimer}
      />
      <PoweredBy>
        Chatbot can make mistakes.<br />Visit <a href="https://nexlydigital.com" target="_blank" rel="noopener noreferrer">nexlydigital.com</a> for details
      </PoweredBy>
    </ChatContainer>
  );
};

export default ChatApp;
